import React, { createRef, forwardRef, MouseEventHandler, useMemo } from "react";

import { bemClassNames, combineRefs } from "@multiweb/reactUtils";
import { ForwardRefType, OnJavaScriptChange } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { EncryptedSymbol } from "../EncryptedSymbol";
import { SelectProvider } from "../SelectProvider";
import {
    FormComponentEventHandlersProps,
    FormComponentProps,
} from "../types";
import { generateFormComponentClasses } from "../utils";
import { ComboBoxOption } from "./types";
import { getOptionProviders } from "./utils";

const classNames = bemClassNames("combobox-component");

export type ComboBoxComponentProps = {
    /** Možnosti pro výběr hodnoty comboboxu  */
    options: ReadonlyArray<ComboBoxOption>;
    /** Přednastavená hodnota comboboxu */
    value?: string | number;
    /** Název comboboxu */
    name?: string;
    /**
     * Callback volaný při změně hodnoty pole.
     * Událost `onChange` je reactí událost a nepodobá se defaultnímu javascriptovému chování události `onChange`.
     * Podobá se spíše javascriptové události `onInput`.
     */
    onChange?: (value: string | undefined) => void;
    /** Callback volaný při odposlechnutí javascriptové `change` eventy */
    onJavaScriptChange?: OnJavaScriptChange;
    /** Callback volaný při kliknutí uvnitř elementu */
    onMouseDown?: MouseEventHandler<HTMLSelectElement>;
    /** Příznak, zda je jako hodnota comboboxu nastavena prázdná hodnota.
     * `isEmpty` může být zvenku definováno nezávisle na tom, jestli je prop `value` undefined, případně do jejího určení můžou vstupovat další proměnné.
     * Např. `isEmpty=true` je u ComboBox komponenty určeno tím, zda je zapnutá multieditace a jaká hodnota `value` je právě nastavena.
     */
    isEmpty?: boolean;
}
& FormComponentProps
& FormComponentEventHandlersProps;

/** Interní komponenta řešící stylování, vytváření pole option providerů a memoizaci */
export const ComboBoxComponent: ForwardRefType<HTMLSelectElement, ComboBoxComponentProps> = forwardRef<HTMLSelectElement, ComboBoxComponentProps>(({
    id,
    options,
    value,
    name,
    disabled = false,
    encrypted = false,
    invalid = false,
    required = false,
    readOnly = false,
    backgroundColor,
    isEmpty,
    onClick,
    onKeyDown,
    ...restProps
}, forwardRef) => {
    const innerRef = createRef<HTMLSelectElement>();
    const ref = combineRefs<HTMLSelectElement>(innerRef, forwardRef);

    const classes = generateFormComponentClasses(classNames, {
        disabled,
        encrypted,
        invalid,
        required,
        backgroundColor,
        readOnly,
    }, [classNames()]);

    const optionComponents = useMemo(() => getOptionProviders(options), [options]);

    return (
        <div role={Role.comboBox} className={classes} {...{ onClick }} onKeyDown={onKeyDown}>
            {encrypted && <EncryptedSymbol/>}
            <SelectProvider
                hidden={encrypted}
                disabled={disabled || encrypted}
                {...{
                    id,
                    value,
                    name,
                    readOnly,
                    required,
                    isEmpty,
                    ref,
                }}
                {...restProps}
            >
                { optionComponents }
            </SelectProvider>
        </div>
    );
});
